import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

class ApiService {
    async makeRequest(endpoint, options = {}) {
        try {
            // Pegar token dos cookies
            const token = Cookies.get('token');

            // Adicionar token JWT no header se existir
            const headers = {
                ...options.headers,
            };

            // Não adicionar Content-Type se for FormData (o browser define automaticamente)
            if (!(options.body instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
            }

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const url = `${API_BASE_URL}${endpoint}`;
            const response = await fetch(url, {
                headers,
                ...options,
            });

            if (!response.ok) {
                let errorMessage;

                // Tentar extrair mensagem de erro do backend primeiro
                try {
                    const errorData = await response.json();
                    // Verificar por 'erro' (português) ou 'error' (inglês)
                    if (errorData.Falha) {
                        // Usar diretamente a mensagem de Falha do backend
                        const err = new Error(errorData.Falha);
                        err.name = "Falha";
                        throw err;
                    } else if (errorData.error) {
                        const err = new Error(errorData.error);
                        err.name = "Falha";
                        throw err;  
                    }
                } catch (parseError) {
                    // Se já é um Error com mensagem, relançar
                    if (parseError instanceof Error && parseError.message && !parseError.message.includes('JSON')) {
                        parseError.name = "Falha"
                        throw parseError;
                    }
                    // Caso contrário, usar mensagem padrão baseada no status
                }

                // Mensagem padrão baseada no status HTTP
                switch (response.status) {
                    case 400:
                        errorMessage = 'Erro na requisição: dados inválidos';
                        break;
                    case 404:
                        errorMessage = 'Recurso não encontrado';
                        break;
                    case 401:
                        errorMessage = 'Não autorizado: Credenciais inválidas';
                        break;
                    case 500:
                        errorMessage = 'Erro interno do servidor';
                        break;
                    case 503:
                        errorMessage = 'Serviço indisponível';
                        break;
                    default:
                        errorMessage = `Erro HTTP: ${response.status}`;
                }

                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error) {
            console.error('Falha na requisição à API:', error);

            // Traduzir erros comuns de rede
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                throw new Error('Erro de conexão: Verifique se o servidor está funcionando');
            } else if (error.name === 'TypeError' && error.message.includes('NetworkError')) {
                throw new Error('Erro de rede: Verifique sua conexão com a internet');
            } else if (error.message.includes('ECONNREFUSED')) {
                throw new Error('Conexão recusada: O servidor não está respondendo');
            }

            throw error;
        }
    }

    async getFilters() {
        return this.makeRequest('/api/filtro/tipos');
    }

    async salvarRodada(rodada) {
        return this.makeRequest('/api/historico', {
            method: 'POST',
            body: JSON.stringify(rodada),
        });
    }

    async listarRodadas() {
        return this.makeRequest('/api/historico');
    }

    async abrirRodada(identificador) {
        return this.makeRequest(`/api/historico/${encodeURIComponent(identificador)}`);
    }

    async runAnalysis(filters = {}) {
        return this.makeRequest('/api/controlador/executar', {
            method: 'POST',
            body: JSON.stringify(filters),
        });
    }

    async runAndreAnalysis(filters = {}) {
        return this.makeRequest('/api/run-andre', {
            method: 'POST',
            body: JSON.stringify(filters),
        });
    }

    async modificarDados(projetosIncluidos, filtros) {
        return this.makeRequest('/api/controlador/modificar', {
            method: 'POST',
            body: JSON.stringify({ projetosIncluidos, filtros }),
        });
    }

    async obterTodosProjetos(filtros = null) {
        return this.makeRequest('/api/repositorio/todos-projetos', {
            method: 'POST',
            body: JSON.stringify(filtros || {})
        });
    }

    async obterTodosProjetosBruto(filtros = null) {
        return this.makeRequest('/api/repositorio/todos-projetos-bruto', {
            method: 'POST',
            body: JSON.stringify(filtros || {})
        });
    }

    async getBasicData() {
        return this.makeRequest('/api/data');
    }

    async login(username, password) {
        return this.makeRequest('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
    }

    async verifyToken(token) {
        return this.makeRequest('/api/auth/verify', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async getCurrentUser() {
        return this.makeRequest('/api/auth/me', {
            method: 'GET',
        });
    }

    // Repositório APIs
    async getRepositorioStatus() {
        return this.makeRequest('/api/repositorio/status');
    }

    async getRepositorioCaminhos() {
        return this.makeRequest('/api/repositorio/caminhos');
    }

    async atualizarRepositorio() {
        return this.makeRequest('/api/repositorio/atualizar', {
            method: 'POST',
        });
    }

    async atualizarRepositorioManual(formData) {
        return this.makeRequest('/api/repositorio/atualizar-manual', {
            method: 'POST',
            body: formData, // FormData com os 3 arquivos CSV
        });
    }

    async restaurarRepositorio(caminho) {
        return this.makeRequest('/api/repositorio/restaurar', {
            method: 'POST',
            body: JSON.stringify({ caminho }),
        });
    }

    async buscarAtualizacaoRepositorio(mensagemLog = '') {
        return this.makeRequest('/api/repositorio/buscar-atualizacao', {
            method: 'POST',
            body: JSON.stringify({ mensagemLog }),
        });
    }

    async listarHistoricoRepositorio() {
        return this.makeRequest('/api/repositorio/listar-historico');
    }

    async obterCaminhoAtualRepositorio() {
        return this.makeRequest('/api/repositorio/caminho-atual');
    }

    async obterTodosPortfolios(filtros = null) {
        return this.makeRequest('/api/repositorio/todos-portfolios', {
            method: 'POST',
            body: JSON.stringify(filtros || {})
        });
    }

    async obterTodosIT(filtros = null) {
        return this.makeRequest('/api/repositorio/todas-iniciativaTec', {
            method: 'POST',
            body: JSON.stringify(filtros || {})
        });
    }

    async obterPortfoliosCorrelatos() {
        return this.makeRequest('/api/repositorio/obter-portfolios-correlatos', {
            method: 'GET'
        });
    }

    async salvarPortfoliosCorrelatos(portfoliosCorrelatos) {
        return this.makeRequest('/api/repositorio/salvar-portfolios-correlatos', {
            method: 'POST',
            body: JSON.stringify({ portfoliosCorrelatos })
        });
    }

    async obterITCorrelatos() {
        return this.makeRequest('/api/repositorio/obter-it-correlatos', {
            method: 'GET'
        });
    }

    async salvarITCorrelatos(itCorrelatos) {
        return this.makeRequest('/api/repositorio/salvar-it-correlatos', {
            method: 'POST',
            body: JSON.stringify({ itCorrelatos })
        });
    }

    // Auth/User APIs
    async registrar(userData) {
        return this.makeRequest('/api/auth/registrar', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    async atualizarPerfil(profileData) {
        return this.makeRequest('/api/auth/atualizar-perfil', {
            method: 'PUT',
            body: JSON.stringify(profileData),
        });
    }

    async alterarSenha(senhaData) {
        return this.makeRequest('/api/auth/alterar-senha', {
            method: 'PUT',
            body: JSON.stringify(senhaData),
        });
    }

    async listarUsuarios() {
        return this.makeRequest('/api/auth/listar-usuarios');
    }

    async atualizarAcesso(email, acesso) {
        return this.makeRequest('/api/auth/atualizar-acesso', {
            method: 'PUT',
            body: JSON.stringify({ email, acesso }),
        });
    }

    // Previsões APIs
    async analisarPrevisoes(codigosProjetos = []) {
        return this.makeRequest('/api/controlador/previsoes/analisar', {
            method: 'POST',
            body: JSON.stringify({ codigosProjetos }),
        });
    }

    async analisarMetas(codigosProjetos = [], trlMeta = 9, crlMeta = 9) {
        return this.makeRequest('/api/controlador/previsoes/analisar', {
            method: 'POST',
            body: JSON.stringify({ codigosProjetos, trlMeta, crlMeta }),
        });
    }

    async verificarStatusTarefa(tarefaId) {
        return this.makeRequest(`/api/controlador/previsoes/status/${tarefaId}`, {
            method: 'GET',
        });
    }

    async obterResumoPrevisoes(resultados) {
        return this.makeRequest('/api/controlador/previsoes/resumo', {
            method: 'POST',
            body: JSON.stringify({ resultados }),
        });
    }

    async calcularPontoDistribuicao(distribuicao, params, valor) {
        return this.makeRequest('/api/controlador/calcular-ponto-distribuicao', {
            method: 'POST',
            body: JSON.stringify({ distribuicao, params, valor }),
        });
    }

    async calcularPercentilSimulacao(simulador, percentil) {
        return this.makeRequest('/api/controlador/simulacao/calcular-percentil', {
            method: 'POST',
            body: JSON.stringify({ simulador, percentil }),
        });
    }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;
